import { signal } from '@preact/signals-react'
import type {
  ConnectedControllers,
  ConnectedControllersDeps,
  ConnectedController,
  ConnectedControllerType,
} from './connectedControllers.types'

function nowMs(): number {
  return Date.now()
}

function makeGamepadControllerId(gamepadIndex: number): string {
  // Keep existing ID scheme stable (X1, X2, ...) to avoid breaking assignment persistence.
  return `X${gamepadIndex + 1}`
}

export function makeConnectedControllers(deps: ConnectedControllersDeps): ConnectedControllers {
  const { controllerInput } = deps

  const controllersSignal = signal<ConnectedController[]>([])
  const lastActiveControllerId = signal<string | null>(null)

  let cleanup: (() => void) | null = null
  let gamepadCleanup: (() => void) | null = null

  function hasControllerChanged(existing: ConnectedController, next: Omit<ConnectedController, 'registeredAt'>): boolean {
    // Only compare the fields we mutate via refresh/upsert.
    if (existing.type !== next.type) return true
    if (existing.gamepadIndex !== next.gamepadIndex) return true
    if (existing.deviceLabel !== next.deviceLabel) return true
    return false
  }

  function upsertController(next: Omit<ConnectedController, 'registeredAt'> & { registeredAt?: number }): void {
    const existing = controllersSignal.value.find((c) => c.id === next.id)
    if (existing) {
      if (!hasControllerChanged(existing, next)) return
      controllersSignal.value = controllersSignal.value.map((c) => (c.id === next.id ? { ...c, ...next } : c))
      return
    }
    controllersSignal.value = [
      ...controllersSignal.value,
      {
        ...next,
        registeredAt: typeof next.registeredAt === 'number' ? next.registeredAt : nowMs(),
      },
    ]
  }

  function removeController(controllerId: string): void {
    controllersSignal.value = controllersSignal.value.filter((c) => c.id !== controllerId)
  }

  function getController(controllerId: string): ConnectedController | undefined {
    return controllersSignal.value.find((c) => c.id === controllerId)
  }

  function refresh(): void {
    if (typeof window === 'undefined' || !navigator.getGamepads) return

    const gamepads = Array.from(navigator.getGamepads()).filter((gp): gp is Gamepad => gp !== null)
    const connectedIndices = new Set(gamepads.map((gp) => gp.index))

    // Add/update gamepads
    for (const gp of gamepads) {
      const controllerId = makeGamepadControllerId(gp.index)
      upsertController({
        id: controllerId,
        type: 'gamepad',
        gamepadIndex: gp.index,
        deviceLabel: gp.id || undefined,
      })
    }

    // Remove disconnected gamepads
    controllersSignal.value
      .filter((c) => c.type === 'gamepad' && typeof c.gamepadIndex === 'number')
      .forEach((c) => {
        if (!connectedIndices.has(c.gamepadIndex!)) {
          removeController(c.id)
        }
      })
  }

  function start(): void {
    if (cleanup) return

    // Initial detection
    refresh()

    // Register keyboard controller when we see raw events from it.
    const unsubscribeRaw = controllerInput.subscribeRaw((event) => {
      lastActiveControllerId.value = event.deviceId

      if (event.deviceId !== 'K') return
      const existing = controllersSignal.value.find((c) => c.id === 'K')
      if (!existing) {
        upsertController({ id: 'K', type: 'keyboard' as ConnectedControllerType })
      }
    })

    const handleGamepadConnected = () => refresh()
    const handleGamepadDisconnected = () => refresh()

    if (typeof window !== 'undefined') {
      window.addEventListener('gamepadconnected', handleGamepadConnected)
      window.addEventListener('gamepaddisconnected', handleGamepadDisconnected)
      const interval = setInterval(refresh, 1000)
      gamepadCleanup = () => {
        window.removeEventListener('gamepadconnected', handleGamepadConnected)
        window.removeEventListener('gamepaddisconnected', handleGamepadDisconnected)
        clearInterval(interval)
      }
    }

    cleanup = () => {
      unsubscribeRaw()
      if (gamepadCleanup) {
        gamepadCleanup()
        gamepadCleanup = null
      }
      cleanup = null
    }
  }

  function stop(): void {
    if (!cleanup) return
    cleanup()
    cleanup = null
  }

  return {
    controllers: controllersSignal,
    lastActiveControllerId,
    start,
    stop,
    refresh,
    getController,
  }
}



